import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  CalendarDays,
  CalendarPlus,
  CheckCircle2,
  ChevronRight,
  FileText,
  Gauge,
  Home,
  Loader2,
  MapPin,
  UserRound,
  Users,
  Wallet,
} from 'lucide-react';
import { fetchTableauRegistre } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { eurosDepuisCentimes as euros } from '../utils/montants';

/**
 * Où en est le club, et ce qui attend quelqu'un.
 *
 * POURQUOI CET ÉCRAN EXISTE
 * ---------------------------------------------------------------------------
 * Le registre comptait cinq écrans — répertoire, cotisations, équipements,
 * équipes, calendrier — et aucun ne disait par où commencer. Un président qui
 * ouvre son espace un dimanche soir devait les visiter l'un après l'autre pour
 * savoir s'il avait quelque chose à faire. Trois écrans pour découvrir qu'il
 * n'y avait rien, ou pour rater ce qu'il y avait.
 *
 * CE QUE CET ÉCRAN N'EST PAS
 * ---------------------------------------------------------------------------
 * Un tableau de chiffres. Chaque bloc répond à une QUESTION, et mène à
 * l'endroit où l'on agit. Un chiffre qui ne mène nulle part se regarde une
 * fois, puis plus jamais.
 *
 * QUAND TOUT VA BIEN, IL LE DIT
 * ---------------------------------------------------------------------------
 * Un bloc sans rien à signaler affiche une ligne verte plutôt que de
 * disparaître. Une carte qui s'efface laisse penser qu'on n'a pas regardé ;
 * « aucun dossier incomplet » dit qu'on a regardé, et que c'est bon.
 */
export default function RegistreTableauPage() {
  const { aLeDroit, isLoading } = useAuth();
  const [saison, setSaison] = useState<string>('');

  const tableau = useQuery({
    queryKey: ['registre-tableau', saison],
    queryFn: () => fetchTableauRegistre(saison === '' ? undefined : Number(saison)),
  });

  if (isLoading) {
    return null;
  }

  if (!aLeDroit('voir_adherents')) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Le registre</h1>
        <p className="text-sm text-gray-500">
          Le registre appartient au bureau et aux entraîneurs. Demandez-leur d'ouvrir ce droit si
          vous en avez l'usage.
        </p>
      </div>
    );
  }

  if (tableau.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const donnees = tableau.data;

  /*
    Sans saison, un seul message et un seul geste.

    Aucun bloc n'a de sens : il n'y a ni licence, ni équipe, ni calendrier. Les
    afficher vides ferait chercher une panne là où il n'y a qu'une saison à
    ouvrir.
  */
  if (donnees?.saison === null || donnees?.saison === undefined) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <CalendarPlus className="w-10 h-10 mx-auto text-gray-300 mb-4" />
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Aucune saison ouverte</h1>
        <p className="text-sm text-gray-500 mb-6">
          {donnees?.message ??
            "C'est le premier geste : ouvrez une saison, et tout le reste en découle."}
        </p>
        <Link
          to="/admin/adherents"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 min-h-[44px]"
        >
          Ouvrir une saison
        </Link>
      </div>
    );
  }

  const { saison: courante, argent, dossiers, licencesEnAttente, groupes, calendrier, rentree } =
    donnees;

  const saisons = donnees.saisons ?? [];

  return (
    <div className="max-w-7xl 2xl:max-w-[100rem] mx-auto px-4 py-8">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Gauge className="w-6 h-6 text-primary-600" />
            Où en est le club
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Saison <strong>{courante.libelle}</strong> · {courante.licencies} licencié
            {courante.licencies > 1 ? 's' : ''}
          </p>
        </div>

        {saisons.length > 1 && (
          <select
            value={saison}
            onChange={(e) => setSaison(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
          >
            {saisons.map((s) => (
              <option key={s.id} value={String(s.id)}>
                {s.libelle}
                {s.courante ? ' — en cours' : ''}
              </option>
            ))}
          </select>
        )}
      </header>

      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3 items-start">
        {/* ------------------------------ L'argent ----------------------------- */}
        <Carte
          titre="Les cotisations"
          icone={Wallet}
          vers="/adherents/cotisations"
          action="Voir le détail"
        >
          <p className="text-3xl font-semibold tabular-nums text-gray-900">
            {euros(argent.reste)}
            <span className="ml-2 text-sm font-normal text-gray-500">à encaisser</span>
          </p>

          {/*
            Le reste seul ne veut rien dire. « 3 200 € » est une catastrophe sur
            3 400 € attendus, et un mois d'octobre ordinaire sur 34 000 €.
          */}
          <p className="mt-1 text-sm text-gray-600">
            {euros(argent.encaisse)} encaissés sur {euros(argent.du)} attendus
          </p>

          {argent.reste === 0 ? (
            <Bon>Tout est réglé.</Bon>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2">
              <Pastille ton={argent.enRetard > 0 ? 'rouge' : 'neutre'}>
                {argent.cotisationsImpayees} cotisation{argent.cotisationsImpayees > 1 ? 's' : ''} à
                relancer
              </Pastille>
              {argent.enRetard > 0 && (
                <Pastille ton="rouge">
                  dont {argent.enRetard} en retard d'échéance
                </Pastille>
              )}
            </div>
          )}
        </Carte>

        {/* ----------------------------- Les dossiers -------------------------- */}
        <Carte titre="Les dossiers" icone={FileText} vers="/adherents" action="Ouvrir le répertoire">
          {dossiers.incomplets === 0 ? (
            <Bon>
              Tous les dossiers sont complets
              {dossiers.reclame > 0 ? ` — ${dossiers.reclame} pièce${dossiers.reclame > 1 ? 's' : ''} réclamée${dossiers.reclame > 1 ? 's' : ''}` : ''}
              .
            </Bon>
          ) : (
            <>
              <p className="text-3xl font-semibold tabular-nums text-gray-900">
                {dossiers.incomplets}
                <span className="ml-2 text-sm font-normal text-gray-500">
                  dossier{dossiers.incomplets > 1 ? 's' : ''} incomplet
                  {dossiers.incomplets > 1 ? 's' : ''}
                </span>
              </p>

              {/*
                Un certificat périmé et un certificat absent produisent la même
                situation : quelqu'un joue sans être couvert. Le premier a été
                déposé, donc le dossier a l'air complet — et c'est celui-là
                qu'on ne voit pas.
              */}
              <p className="mt-1 text-xs text-gray-500">
                Pièce manquante ou périmée. Un certificat périmé compte : le licencié n'est plus
                couvert.
              </p>

              <ul className="mt-3 space-y-1 text-sm">
                {dossiers.apercu.map((a) => (
                  <li key={a.id}>
                    <Link
                      to={`/adherents/${a.id}`}
                      className="text-gray-800 hover:text-primary-700"
                    >
                      {a.nomComplet}
                    </Link>
                    <span className="text-xs text-gray-500"> · {a.manquants.join(', ')}</span>
                  </li>
                ))}
              </ul>

              {dossiers.incomplets > dossiers.apercu.length && (
                <p className="mt-1 text-xs text-gray-500">
                  et {dossiers.incomplets - dossiers.apercu.length} autre
                  {dossiers.incomplets - dossiers.apercu.length > 1 ? 's' : ''}
                </p>
              )}
            </>
          )}
        </Carte>

        {/* -------------------------- Licences en attente ---------------------- */}
        <Carte
          titre="Licences en attente"
          icone={UserRound}
          vers="/adherents/cotisations"
          action="Voir les licences"
        >
          {licencesEnAttente.total === 0 ? (
            <Bon>Toutes les licences sont validées.</Bon>
          ) : (
            <>
              <p className="text-3xl font-semibold tabular-nums text-gray-900">
                {licencesEnAttente.total}
                <span className="ml-2 text-sm font-normal text-gray-500">
                  chez la fédération
                </span>
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Une licence naît en attente : vous recopiez le numéro quand la fédération l'a rendu.
                Un joueur sans licence délivrée ne joue pas.
              </p>

              <ul className="mt-3 space-y-1 text-sm">
                {licencesEnAttente.apercu.map((l) => (
                  <li key={l.id} className="flex items-baseline justify-between gap-3">
                    <Link
                      to={`/adherents/${l.adherentId}`}
                      className="text-gray-800 hover:text-primary-700"
                    >
                      {l.nomComplet}
                    </Link>
                    <span className="text-xs text-gray-500">
                      {l.discipline}
                      {l.sansNumero ? ' · sans numéro' : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Carte>

        {/* ------------------------------ Les groupes -------------------------- */}
        <Carte titre="Les équipes" icone={Users} vers="/adherents/equipes" action="Composer">
          <p className="text-3xl font-semibold tabular-nums text-gray-900">
            {groupes.total}
            <span className="ml-2 text-sm font-normal text-gray-500">
              équipe{groupes.total > 1 ? 's' : ''} cette saison
            </span>
          </p>

          {groupes.vides === 0 && groupes.sansDiscipline === 0 ? (
            <Bon>Toutes sont composées.</Bon>
          ) : (
            <>
              {groupes.vides > 0 && (
                <>
                  {/*
                    Une équipe créée en septembre et jamais composée se
                    découvre au premier match : elle existe, elle a un nom,
                    elle apparaît dans les listes.
                  */}
                  <p className="mt-3 text-sm text-amber-800">
                    {groupes.vides} équipe{groupes.vides > 1 ? 's' : ''} sans personne dedans :
                  </p>
                  <ul className="mt-1 space-y-0.5 text-sm text-gray-700">
                    {groupes.apercu.map((e) => (
                      <li key={e.id}>{e.nom}</li>
                    ))}
                  </ul>
                </>
              )}

              {groupes.sansDiscipline > 0 && (
                <p className="mt-3 text-sm text-amber-800">
                  {groupes.sansDiscipline} équipe{groupes.sansDiscipline > 1 ? 's' : ''} sans
                  discipline : elle{groupes.sansDiscipline > 1 ? 's' : ''} vous propose
                  {groupes.sansDiscipline > 1 ? 'nt' : ''} tous les licenciés du club au lieu de
                  ceux de la section.
                </p>
              )}
            </>
          )}
        </Carte>

        {/* ----------------------------- Le calendrier ------------------------- */}
        <Carte
          titre="Les prochaines rencontres"
          icone={CalendarDays}
          vers="/adherents/rencontres"
          action="Ouvrir le calendrier"
          large
        >
          {calendrier.prochaines.length === 0 ? (
            <Bon>Rien au calendrier pour l'instant.</Bon>
          ) : (
            <>
              {calendrier.sansGroupe > 0 && (
                <p className="mb-3 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    {calendrier.sansGroupe} rencontre{calendrier.sansGroupe > 1 ? 's' : ''} à venir
                    n'{calendrier.sansGroupe > 1 ? 'ont' : 'a'} convoqué personne.
                  </span>
                </p>
              )}

              <ul className="divide-y divide-gray-100">
                {calendrier.prochaines.map((r) => (
                  <li
                    key={r.id}
                    className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2 first:pt-0 last:pb-0"
                  >
                    <span className="text-sm text-gray-800">
                      <span className="inline-flex items-center gap-1 text-gray-400">
                        {r.aDomicile ? (
                          <Home className="w-3.5 h-3.5" />
                        ) : (
                          <MapPin className="w-3.5 h-3.5" />
                        )}
                      </span>{' '}
                      {r.aDomicile ? 'Contre' : 'Chez'} {r.adversaire}
                      {r.equipe && <span className="text-gray-500"> · {r.equipe}</span>}
                    </span>

                    <span className="flex items-center gap-3 text-xs">
                      <span className="text-gray-500 first-letter:uppercase">{quand(r.debut)}</span>
                      <span className={r.convoques === 0 ? 'text-amber-700' : 'text-gray-500'}>
                        {r.convoques === 0
                          ? 'personne convoqué'
                          : `${r.convoques} convoqué${r.convoques > 1 ? 's' : ''}`}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Carte>

        {/* ------------------------------ La rentrée --------------------------- */}
        {rentree.saisonPrecedente !== null && (
          <Carte titre="Ils ne sont pas revenus" icone={CalendarPlus} vers="/adherents" action="Ouvrir le répertoire">
            {rentree.total === 0 ? (
              <Bon>Tous les licenciés de {rentree.saisonPrecedente} ont repris.</Bon>
            ) : (
              <>
                <p className="text-3xl font-semibold tabular-nums text-gray-900">
                  {rentree.total}
                  <span className="ml-2 text-sm font-normal text-gray-500">à rappeler</span>
                </p>

                {/*
                  Le bloc le plus utile de cet écran.

                  Une famille qui ne se réinscrit pas ne dit rien : elle ne se
                  plaint pas, elle n'écrit pas, elle ne revient simplement pas.
                  Le club s'en aperçoit en septembre, quand il manque huit
                  joueurs — et il est trop tard pour appeler.
                */}
                <p className="mt-1 text-xs text-gray-500">
                  Licenciés en {rentree.saisonPrecedente}, pas encore réinscrits. Un appel en juin
                  vaut mieux qu'un constat en septembre.
                </p>

                <ul className="mt-3 space-y-1 text-sm">
                  {rentree.apercu.map((a) => (
                    <li key={a.id} className="flex flex-wrap items-baseline justify-between gap-x-3">
                      <Link
                        to={`/adherents/${a.id}`}
                        className="text-gray-800 hover:text-primary-700"
                      >
                        {a.nomComplet}
                      </Link>
                      <span className="text-xs text-gray-500">
                        {a.telephone ?? a.email ?? 'aucune coordonnée'}
                      </span>
                    </li>
                  ))}
                </ul>

                {rentree.total > rentree.apercu.length && (
                  <p className="mt-1 text-xs text-gray-500">
                    et {rentree.total - rentree.apercu.length} autre
                    {rentree.total - rentree.apercu.length > 1 ? 's' : ''}
                  </p>
                )}
              </>
            )}
          </Carte>
        )}
      </div>
    </div>
  );
}

/* -------------------------------- Les pièces ----------------------------- */

/**
 * Une carte du tableau : une question, sa réponse, et où agir.
 *
 * Le lien est en pied, pas en titre : on lit d'abord ce qu'il y a, puis on
 * décide d'y aller. Un titre cliquable fait partir avant d'avoir lu.
 */
function Carte({
  titre,
  icone: Icone,
  vers,
  action,
  large = false,
  children,
}: {
  titre: string;
  icone: React.ComponentType<{ className?: string }>;
  vers: string;
  action: string;
  large?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section
      className={`bg-white rounded-xl border border-gray-200 p-5 flex flex-col ${
        large ? 'lg:col-span-2' : ''
      }`}
    >
      <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3">
        <Icone className="w-4 h-4 text-primary-600" />
        {titre}
      </h2>

      <div className="flex-1">{children}</div>

      <Link
        to={vers}
        className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:underline"
      >
        {action}
        <ChevronRight className="w-4 h-4" />
      </Link>
    </section>
  );
}

/**
 * Quand il n'y a rien à signaler, on le dit.
 *
 * Une carte qui s'efface laisse penser qu'on n'a pas regardé. « Aucun dossier
 * incomplet » dit qu'on a regardé, et que c'est bon — ce qui est précisément
 * l'information qu'un président vient chercher un dimanche soir.
 */
function Bon({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-2 flex items-start gap-2 text-sm text-green-800">
      <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-green-600" />
      <span>{children}</span>
    </p>
  );
}

function Pastille({ ton, children }: { ton: 'rouge' | 'neutre'; children: React.ReactNode }) {
  return (
    <span
      className={`rounded-full px-2.5 py-1 text-xs font-medium ${
        ton === 'rouge' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
      }`}
    >
      {children}
    </span>
  );
}

function quand(iso: string | null): string {
  if (iso === null) {
    return '';
  }

  return new Date(iso).toLocaleString('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
